import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Panel from '@/components/ui/Panel';
import Toast from '@/components/ui/Toast';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** Портал → «Команда»: сотрудники партнёра (реальные членства), приглашение (Owner), настройки уведомлений своего профиля. */
type Role = 'Owner' | 'Warehouse' | 'Sales' | 'Accounting';
type Member = { id: string; profileId: string; name: string; email: string; role: Role; status: 'Active' | 'Invited' | 'Disabled'; lastLoginAt: string | null };
type Prefs = Record<string, { email: boolean; whatsapp: boolean }>;
const EVENT_KEYS = ['evOrders', 'evPayments', 'evReservations', 'evStock', 'evWarranty', 'evPriceList'];

function NotifSwitch({ checked, onChange, label }: { checked: boolean; onChange: (next: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 cursor-pointer rounded-full transition-colors ${checked ? 'bg-primary-500' : 'bg-background-400'}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-background-50 transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  );
}

export default function PortalTeamPage() {
  const { t, i18n } = useTranslation();
  const { me } = useAuth();
  const isOwner = me?.role === 'Owner';
  const [members, setMembers] = useState<Member[] | null>(null);
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('Warehouse');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [toast, setToast] = useState<{ message: string; detail?: string; tone?: 'success' | 'error' } | null>(null);
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-GB';

  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, p] = await Promise.all([api.get<Member[]>('/team'), api.get<Prefs>('/team/prefs')]);
      setMembers(m);
      setPrefs(p);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setFormError('');
    try {
      await api.post('/team/invite', { email: email.trim(), name: name.trim() || undefined, role, locale: i18n.language === 'ru' ? 'ru' : 'en' });
      setOpen(false);
      setToast({ message: t('portal.team.invitedToast', { email: email.trim() }) });
      setName('');
      setEmail('');
      setRole('Warehouse');
      await load();
    } catch (e) {
      const code = (e as ApiError).code;
      setFormError(code === 'other_partner' || code === 'staff_email' ? t('auth.accessByInvite') : `${t('ui.error')}: ${code}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleMember = async (m: Member) => {
    try {
      await api.patch(`/team/${m.id}`, { status: m.status === 'Disabled' ? 'Active' : 'Disabled' });
      await load();
    } catch (e) {
      setToast({ message: t('ui.error'), detail: (e as ApiError).code, tone: 'error' });
    }
  };

  const setChannel = (key: string, channel: 'email' | 'whatsapp', value: boolean) => {
    setPrefs((prev) => (prev ? { ...prev, [key]: { ...prev[key], [channel]: value } } : prev));
  };

  const saveNotifications = async () => {
    if (!prefs) return;
    try {
      setPrefs(await api.put<Prefs>('/team/prefs', { prefs }));
      setToast({ message: t('portal.team.notifSaved') });
    } catch (e) {
      setToast({ message: t('ui.error'), detail: (e as ApiError).code, tone: 'error' });
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.team.title')}</h1>
          <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.team.subtitle')}</p>
        </div>
        {isOwner && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-8 w-fit items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-user-add-line text-[14px]" />
            {t('portal.team.invite')}
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
          {t('ui.error')}: {error}{' '}
          <button type="button" onClick={() => void load()} className="cursor-pointer font-semibold underline">
            {t('ui.retry')}
          </button>
        </p>
      )}

      <Panel padded={false}>
        <div className="flex flex-col gap-1 border-b border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.team.title')}</h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.team.note')}</p>
          </div>
          <span className="tnum rounded-full border border-background-200 px-2 py-0.5 text-[10.5px] text-foreground-500">
            {t('portal.team.seats', { used: members?.length ?? 0, total: 8 })}
          </span>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[720px] border-collapse text-[12px]">
            <thead>
              <tr className="bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('portal.team.colName')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.team.colEmail')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.team.colRole')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.team.colLast')}</th>
                <th className="px-4 py-2.5 font-medium">{t('portal.team.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {!members && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-foreground-400">
                    {t('ui.loading')}
                  </td>
                </tr>
              )}
              {members?.map((m) => (
                <tr key={m.id} className="border-t border-background-200/70 hover:bg-background-100/40">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[10px] font-semibold text-secondary-800">
                        {m.name
                          .split(' ')
                          .map((p) => p[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </span>
                      <span className="text-[11.5px] font-medium text-foreground-900">
                        {m.name}
                        {m.profileId === me?.id && <span className="ml-1 text-[10px] text-foreground-400">({t('users.you')})</span>}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-foreground-600">{m.email}</td>
                  <td className="px-3 py-2.5">
                    <span className="rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[10px] font-medium text-foreground-700">{t(`portal.team.role${m.role}`)}</span>
                  </td>
                  <td className="tnum px-3 py-2.5 text-[10.5px] text-foreground-500">{m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleString(locale) : t('portal.team.lastNever')}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <PortalStatus status={m.status === 'Disabled' ? 'Expired' : m.status} label={m.status === 'Disabled' ? t('users.statusDisabled') : undefined} />
                      {isOwner && m.profileId !== me?.id && (
                        <button type="button" onClick={() => void toggleMember(m)} className="cursor-pointer text-[10.5px] font-medium text-foreground-500 hover:text-primary-700">
                          {m.status === 'Disabled' ? t('users.enable') : t('users.disable')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel className="mt-4" padded={false}>
        <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.team.notifTitle')}</h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.team.notifSub')}</p>
          </div>
          <button
            type="button"
            onClick={() => void saveNotifications()}
            disabled={!prefs}
            className="inline-flex h-7 w-fit items-center gap-1.5 whitespace-nowrap rounded border border-background-300 bg-background-50 px-2.5 text-[10.5px] font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-50"
          >
            <i className="ri-save-3-line text-[12px]" />
            {t('portal.common.save')}
          </button>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[560px] border-collapse text-[12px]">
            <thead>
              <tr className="bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2 font-medium">{t('portal.team.notifEvent')}</th>
                <th className="px-3 py-2 font-medium">{t('portal.team.notifEmail')}</th>
                <th className="px-4 py-2 font-medium">{t('portal.team.notifWhatsapp')}</th>
              </tr>
            </thead>
            <tbody>
              {EVENT_KEYS.map((key) => (
                <tr key={key} className="border-t border-background-200/70">
                  <td className="px-4 py-2.5 text-[11.5px] text-foreground-800">{t(`portal.team.${key}`)}</td>
                  <td className="px-3 py-2.5">
                    <NotifSwitch checked={prefs?.[key]?.email ?? true} onChange={(v) => setChannel(key, 'email', v)} label={`${t(`portal.team.${key}`)} · ${t('portal.team.notifEmail')}`} />
                  </td>
                  <td className="px-4 py-2.5">
                    <NotifSwitch checked={prefs?.[key]?.whatsapp ?? false} onChange={(v) => setChannel(key, 'whatsapp', v)} label={`${t(`portal.team.${key}`)} · ${t('portal.team.notifWhatsapp')}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-[14px] font-semibold text-foreground-950">{t('portal.team.inviteTitle')}</h2>
            <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('portal.team.inviteSub')}</p>
            <div className="mt-3 flex flex-col gap-3">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('portal.team.inviteName')}</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] outline-none focus:border-primary-400" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('portal.team.inviteEmail')}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.ae" className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] outline-none focus:border-primary-400" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('portal.team.inviteRole')}</span>
                <select value={role} onChange={(e) => setRole(e.target.value as Role)} className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400">
                  {(['Owner', 'Warehouse', 'Sales', 'Accounting'] as const).map((r) => (
                    <option key={r} value={r}>
                      {t(`portal.team.role${r}`)}
                    </option>
                  ))}
                </select>
              </label>
              {formError && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">{formError}</p>}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                {t('portal.common.cancel')}
              </button>
              <button type="button" onClick={() => void invite()} disabled={busy || !email.trim()} className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60">
                {busy ? t('fb.sending') : t('portal.team.inviteSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
